import { FeatureFlagsMap } from 'inquiry/lib/interfaces/feature-flags';
import { isIOS16Phone, isSafari16 } from 'lib/utils/device';
import { disposeStream } from 'lib/utils/stream';
import { HolderClient } from 'verify/lib/clients/HolderClient';

class Client {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private stream: MediaStream;

  /**
   * Prepares user media, and the video stream as well by attaching it to a video element.
   *
   * If the user has not granted camera permissions, this will trigger a dialog.
   */
  public async prepare(mediaConstraints: MediaStreamConstraints, featureFlags: FeatureFlagsMap) {
    if (this.stream != null) {
      return;
    }

    // This line triggers the permissions dialog.
    // Can raise:
    // - Permission denied: PermissionDeniedError | NotAllowedError
    // - Camera not available: SourceUnavailableError
    this.stream = await navigator.mediaDevices.getUserMedia(mediaConstraints);

    // safari / ios 16 has issues when using a stream that is not shown
    // so exit early if FF is turned on and we are safari 16 / ios 16
    if (
      isSafari16() ||
      isIOS16Phone() ||
      HolderClient.isWebView ||
      featureFlags?.['inquiry:dispose-video-client-early-all-web-clients']
    ) {
      this.dispose();
      return;
    }

    const videoElement = document.createElement('video') as HTMLVideoElement;
    let videoStreamUrl: string;

    /* eslint-disable @persona-id/eqeq-null-undefined */
    /* eslint-disable @typescript-eslint/ban-ts-comment */
    // @ts-ignore - Checking that this value !== undefined is how we check whether or not this is supported.
    // null means it is supported.
    if (videoElement.srcObject !== undefined) {
      /* eslint-enable @persona-id/eqeq-null-undefined */
      /* eslint-enable @typescript-eslint/ban-ts-comment */
      videoElement.srcObject = this.stream;
    } else {
      // Approach for legacy browsers. Types are different than what lib.dom.ts reports.
      videoStreamUrl = window.URL.createObjectURL(this.stream as unknown as MediaSource);
      videoElement.src = videoStreamUrl;
    }

    // Don't need to await this; we can load the next UI and have this resolve
    // asynchronously.
    new Promise<void>((resolve) => {
      const handleLoadedMetadata = () => {
        window.URL.revokeObjectURL(videoStreamUrl);
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);

        resolve();
      };

      // NB: `canplay` and `loadeddata` are not triggered in Firefox if element isn't added.
      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    });
  }

  /**
   * Cleans up the stream used for preparation.
   * As of v5, react-webcam handles some of this for us. However it doesn't handle `getTracks()`, and `stop()` is idempotent, so we keep this check for safety (e.g. if we ever migrate to another lib).
   */
  public dispose() {
    disposeStream(this.stream);

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.stream = undefined;
  }
}

export const VideoClient = new Client();
